import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./DControlHistory.css";

const DEFAULT_CENTER = [-33.86, 151.2];

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
// Fix for leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom marker icon for current position
const createCustomIcon = () => {
  return L.divIcon({
    className: 'current-marker',
    html: `
      <div class="marker-pulse">
        <div class="marker-inner">
          <i class="bi bi-truck"></i>
        </div>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });
};

// Safe component to fit bounds
const FitBoundsOnRoute = ({ positions, isMapReady }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !positions || positions.length === 0 || !isMapReady) return;

    const fitBounds = () => {
      try {
        if (positions.length === 1) {
          map.setView(positions[0], 18);
        } else {
          const bounds = L.latLngBounds(positions);
          map.fitBounds(bounds, { 
            padding: [40, 40],
            animate: false
          });
        }
      } catch (e) {
        console.warn("Fit bounds error:", e);
      }
    };

    // Use timeout to ensure map is fully initialized
    const timeoutId = setTimeout(fitBounds, 100);
    
    return () => clearTimeout(timeoutId);
  }, [map, positions, isMapReady]);

  return null;
};

const DControlHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [animationTrackKey, setAnimationTrackKey] = useState(0);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);

  const {
    startDate,
    endDate,
    rego,
    organizationId,
    fleetData: incomingFleetData = [],
    latLngArray: incomingLatLngArray = [],
  } = location.state || {};

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showWeightChart, setShowWeightChart] = useState(true);

  const fleetData = incomingFleetData || [];

  // Safe route positions calculation
  const routeArray = useMemo(() => {
    try {
      if (incomingLatLngArray && Array.isArray(incomingLatLngArray) && incomingLatLngArray.length > 0) {
        return incomingLatLngArray;
      }
      
      if (!fleetData || !Array.isArray(fleetData)) return [];
      
      return fleetData
        .filter(p => p && 
          typeof p.latitude === "number" && 
          typeof p.longitude === "number" &&
          !isNaN(p.latitude) && 
          !isNaN(p.longitude))
        .map(p => [p.latitude, p.longitude]);
    } catch (error) {
      console.error("Error calculating route array:", error);
      return [];
    }
  }, [incomingLatLngArray, fleetData]);

  // Calculate weight percentages safely
  const getWeightPercentages = (point) => {
    if (!point || !point.totalWeight || point.totalWeight <= 0) return {};
    try {
      return {
        steer: ((point.steerWeight / point.totalWeight) * 100).toFixed(1),
        drive: ((point.driveWeight / point.totalWeight) * 100).toFixed(1),
        trailerA: ((point.trailerAWeight / point.totalWeight) * 100).toFixed(1),
        trailerB: ((point.trailerBWeight / point.totalWeight) * 100).toFixed(1),
      };
    } catch (error) {
      console.error("Error calculating weight percentages:", error);
      return {};
    }
  };

  // Clamp index on data change
  useEffect(() => {
    if (!fleetData.length) {
      setCurrentIndex(0);
      setIsPlaying(false);
      return;
    }
    setCurrentIndex(prev => {
      if (prev >= fleetData.length) return fleetData.length - 1;
      if (prev < 0) return 0;
      return prev;
    });
    setAnimationTrackKey(k => k + 1);
  }, [fleetData.length]);

  const currentPoint = fleetData[currentIndex] || null;
  const totalPoints = fleetData.length;
  const weightPercentages = currentPoint ? getWeightPercentages(currentPoint) : {};

  // Playback with safe interval
  useEffect(() => {
    if (!isPlaying || totalPoints <= 1) return;

    const baseStepMs = 1000;
    const intervalMs = baseStepMs / playbackSpeed;

    const id = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= totalPoints - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);

    return () => clearInterval(id);
  }, [isPlaying, playbackSpeed, totalPoints]);

  // Map ready handler
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMapReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Handle map errors
  useEffect(() => {
    const handleMapError = (e) => {
      console.error("Map error:", e);
      setMapError(true);
    };

    window.addEventListener('error', handleMapError);
    
    return () => {
      window.removeEventListener('error', handleMapError);
    };
  }, []);

  const handlePlayPause = () => {
    if (!totalPoints) return;
    setIsPlaying(prev => !prev);
  };

  const handleStep = (direction) => {
    setIsPlaying(false);
    setCurrentIndex(prev => {
      const next = prev + direction;
      if (next < 0) return 0;
      if (next >= totalPoints) return totalPoints - 1;
      return next;
    });
  };

  const handleSliderChange = (e) => {
    const value = Number(e.target.value);
    if (isNaN(value)) return;
    
    setCurrentIndex(value);
    setIsPlaying(false);
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const d = new Date(isoString);
      if (Number.isNaN(d.getTime())) return isoString;
      return d.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    } catch (error) {
      console.error("Error formatting time:", error);
      return "-";
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    try {
      const d = new Date(isoString);
      if (Number.isNaN(d.getTime())) return isoString;
      return d.toLocaleDateString();
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "-";
    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    });
  };

  // Safe center calculation
  const center = useMemo(() => {
    try {
      if (routeArray.length > 0) {
        const idx = Math.min(currentIndex, routeArray.length - 1);
        const point = routeArray[idx] || routeArray[0];
        if (Array.isArray(point) && point.length >= 2) {
          return [point[0], point[1]];
        }
      }
      return DEFAULT_CENTER;
    } catch (error) {
      console.error("Error calculating center:", error);
      return DEFAULT_CENTER;
    }
  }, [routeArray, currentIndex]);

  const progressPercent = totalPoints > 1 
    ? Math.round((currentIndex / (totalPoints - 1)) * 100) 
    : 0;

  // If user hits page directly
  if (!location.state || !fleetData.length) {
    return (
      <div className="dch-root dch-root-empty">
        <div className="dch-card dch-empty-card">
          <div className="empty-state-icon">
            <i className="bi bi-truck" style={{ fontSize: '2rem' }}></i>
          </div>
          <h1>No Playback Data</h1>
          <p>Please select a vehicle and time range to view journey playback.</p>
          <button
            type="button"
            className="dch-btn dch-btn-primary"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-chevron-double-left"></i>
            Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dch-root">
      {/* LEFT: Controls & info */}
      <aside className="dch-sidebar">
        <header className="dch-header">
          <div className="header-main">
            <div className="dch-breadcrumb">
              <button 
                className="back-button"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-chevron-double-left"></i>
                Back
              </button>
              <span className="breadcrumb-separator">/</span>
              <span>Journey Playback</span>
            </div>
            <h1 className="dch-title">Vehicle Journey Timeline</h1>
            <div className="vehicle-info">
              <div className="vehicle-rego">
                <i className="bi bi-truck"></i>
                <span>{"N/A"}</span>
              </div>
              <div className="org-badge">
                {organizationId || "N/A"}
              </div>
            </div>
          </div>
          <div className="date-range">
            <i className="bi bi-calendar"></i>
            <span>
              {startDate ? formatDate(startDate) : "N/A"} – {endDate ? formatDate(endDate) : "N/A"}
            </span>
          </div>
        </header>

        {/* Timeline & Playback Controls */}
        <section className="dch-section timeline-section">
          <div className="section-header">
            <h2>
              <i className="bi bi-clock"></i>
              Timeline
            </h2>
            <div className="progress-stats">
              <span className="progress-text">{progressPercent}% Complete</span>
              <span className="points-count">{totalPoints} points</span>
            </div>
          </div>

          <div className="timeline-slider-container">
            <div className="slider-labels">
              <span>{formatTime(fleetData[0]?.timestamp)}</span>
              <span>{formatTime(fleetData[totalPoints - 1]?.timestamp)}</span>
            </div>
            <div className="custom-slider">
              <input
                type="range"
                min={0}
                max={Math.max(totalPoints - 1, 0)}
                value={currentIndex}
                onChange={handleSliderChange}
                disabled={!totalPoints}
                className="slider-input"
              />
              <div className="slider-track">
                <div 
                  className="slider-progress"
                  style={{ width: `${progressPercent}%` }}
                >
                  <div className="slider-thumb"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="playback-controls">
            <div className="playback-buttons">
              <button
                type="button"
                className="control-btn secondary"
                onClick={() => handleStep(-10)}
                disabled={!totalPoints || currentIndex === 0}
              >
                <i className="bi bi-skip-backward"></i>
                15s
              </button>
              <button
                type="button"
                className="control-btn primary"
                onClick={handlePlayPause}
                disabled={!totalPoints}
              >
                {isPlaying ? (
                  <>
                    <i className="bi bi-pause"></i>
                    Pause
                  </>
                ) : (
                  <>
                    <i className="bi bi-play"></i>
                    Play
                  </>
                )}
              </button>
              <button
                type="button"
                className="control-btn secondary"
                onClick={() => handleStep(10)}
                disabled={!totalPoints || currentIndex === totalPoints - 1}
              >
                15s
                <i className="bi bi-skip-forward"></i>
              </button>
            </div>

            <div className="speed-controls">
              <label>Playback Speed:</label>
              <div className="speed-buttons">
                {[0.5, 1, 2, 4].map((speed) => (
                  <button
                    key={speed}
                    className={`speed-btn ${playbackSpeed === speed ? 'active' : ''}`}
                    onClick={() => setPlaybackSpeed(speed)}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Current Snapshot */}
        <section className="dch-section snapshot-section">
          <div className="section-header">
            <h2>
              <i className="bi bi-clock-history"></i>
              Current Snapshot
            </h2>
            <div className="snapshot-meta">
              Point {currentIndex + 1} of {totalPoints}
            </div>
          </div>

          {currentPoint && (
            <div className="snapshot-content">
              <div className="info-grid">
                <div className="info-card">
                  <div className="info-header">
                    <div className="info-icon">
                      <i className="bi bi-truck"></i>
                    </div>
                    <div className="info-title">
                      <h3>Vehicle</h3>
                      <div className={`status-badge ${currentPoint.status}`}>
                        <i className={`bi bi-circle-fill ${currentPoint.status === 'online' ? 'online' : 'offline'}`}></i>
                        {currentPoint.status || "unknown"}
                      </div>
                    </div>
                  </div>
                  <div className="info-body">
                    <div className="info-row">
                      <span className="label">Registration</span>
                      <span className="value">{currentPoint.vehicleId || "-"}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Driver</span>
                      <span className="value">{currentPoint.empId || "-"}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Speed</span>
                      <span className="value highlight">
                        {formatNumber(currentPoint.speed)}
                        <span className="unit"> km/h</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-header">
                    <div className="info-icon">
                      <i className="bi bi-speedometer"></i>
                    </div>
                    <div className="info-title">
                      <h3>Weight Distribution</h3>
                      <button 
                        className="toggle-chart"
                        onClick={() => setShowWeightChart(!showWeightChart)}
                      >
                        <i className={`bi bi-${showWeightChart ? 'eye-slash' : 'eye'}`}></i>
                        {showWeightChart ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  <div className="info-body">
                    <div className="total-weight">
                      <span className="label">Total Weight</span>
                      <span className="value">
                        {formatNumber(currentPoint.totalWeight)} {currentPoint.scaleUnit || "kg"}
                      </span>
                    </div>
                    
                    {showWeightChart && (
                      <div className="weight-chart">
                        {[
                          { 
                            label: 'Steer', 
                            value: currentPoint.steerWeight || 0, 
                            percentage: weightPercentages.steer || "0", 
                            color: '#3B82F6' 
                          },
                          { 
                            label: 'Drive', 
                            value: currentPoint.driveWeight || 0, 
                            percentage: weightPercentages.drive || "0", 
                            color: '#10B981' 
                          },
                          (currentPoint.trailerAWeight > 0) && { 
                            label: 'Trailer A', 
                            value: currentPoint.trailerAWeight, 
                            percentage: weightPercentages.trailerA || "0", 
                            color: '#F59E0B' 
                          },
                          (currentPoint.trailerBWeight > 0) && { 
                            label: 'Trailer B', 
                            value: currentPoint.trailerBWeight, 
                            percentage: weightPercentages.trailerB || "0", 
                            color: '#EF4444' 
                          }
                        ].filter(Boolean).map((item, index) => (
                          <div key={index} className="weight-row">
                            <div className="weight-label">
                              <span className="weight-color" style={{ backgroundColor: item.color }}></span>
                              {item.label}
                            </div>
                            <div className="weight-bar-container">
                              <div 
                                className="weight-bar"
                                style={{ 
                                  width: `${item.percentage}%`,
                                  backgroundColor: item.color
                                }}
                              >
                                <span className="weight-percentage">{item.percentage}%</span>
                              </div>
                            </div>
                            <div className="weight-value">
                              {formatNumber(item.value)} {currentPoint.scaleUnit || "kg"}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="info-card location-card">
                  <div className="info-header">
                    <div className="info-icon">
                      <i className="bi bi-geo-alt"></i>
                    </div>
                    <div className="info-title">
                      <h3>Location & Time</h3>
                    </div>
                  </div>
                  <div className="info-body">
                    <div className="info-row">
                      <span className="label">Timestamp</span>
                      <span className="value">{formatTime(currentPoint.timestamp)}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Date</span>
                      <span className="value">{formatDate(currentPoint.timestamp)}</span>
                    </div>
                    <div className="location-details">
                      <div className="coordinates">
                        <i className="bi bi-compass"></i>
                        <span>
                          {formatNumber(currentPoint.latitude)}, {formatNumber(currentPoint.longitude)}
                        </span>
                      </div>
                      <div className="address">
                        <i className="bi bi-house"></i>
                        {currentPoint.address || "No address available"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat-card">
            <i className="bi bi-speedometer2"></i>
            <div className="stat-content">
              <div className="stat-value">
                {fleetData.length > 0 
                  ? `${Math.round(fleetData.reduce((sum, p) => (sum + (p.speed || 0)), 0) / fleetData.length)} km/h`
                  : 'N/A'
                }
              </div>
              <div className="stat-label">Average Speed</div>
            </div>
          </div>
          <div className="stat-card">
            <i className="bi bi-clock"></i>
            <div className="stat-content">
              <div className="stat-value">
                {fleetData.length > 1 
                  ? `${Math.round((new Date(fleetData[fleetData.length-1]?.timestamp || Date.now()) - new Date(fleetData[0]?.timestamp || Date.now())) / 60000)} min`
                  : 'N/A'
                }
              </div>
              <div className="stat-label">Duration</div>
            </div>
          </div>
        </div>
      </aside>

      {/* RIGHT: Map */}
      <main className="dch-map-shell">
        <div className="dch-map-container">
          <div className="map-header">
            <div className="map-title">
              <h2>Journey Route</h2>
              <div className="map-stats">
                <span className="stat">
                  <i className="bi bi-geo-alt"></i>
                  {routeArray.length} locations
                </span>
                <span className="stat">
                  <i className="bi bi-clock"></i>
                  {fleetData.length > 1 
                    ? `${Math.round((new Date(fleetData[fleetData.length-1]?.timestamp || Date.now()) - new Date(fleetData[0]?.timestamp || Date.now())) / 60000)} min`
                    : 'N/A'
                  }
                </span>
              </div>
            </div>
            <div className="map-controls">
              <div className="current-time">
                <i className="bi bi-clock"></i>
                {currentPoint ? formatTime(currentPoint.timestamp) : "No data"}
              </div>
            </div>
          </div>

          <div className="map-wrapper" ref={mapContainerRef}>
            {mapError ? (
              <div className="map-error">
                <i className="bi bi-exclamation-triangle"></i>
                <p>Unable to load map. Please try refreshing the page.</p>
              </div>
            ) : (
              <MapContainer
                key={`map-${animationTrackKey}`}
                ref={mapRef}
                center={center}
                zoom={18}
                className="dch-map"
                zoomControl={false}
                whenCreated={(mapInstance) => {
                  mapRef.current = mapInstance;
                  // Force a reflow to ensure map renders properly
                  setTimeout(() => {
                    if (mapInstance && mapInstance.invalidateSize) {
                      mapInstance.invalidateSize();
                    }
                  }, 100);
                }}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {routeArray.length > 0 && (
                  <>
                    <FitBoundsOnRoute 
                      positions={routeArray} 
                      isMapReady={isMapReady}
                    />
                    <Polyline 
                      positions={routeArray} 
                      pathOptions={{
                        color: '#3B82F6',
                        weight: 3,
                        opacity: 0.7,
                        lineCap: 'round'
                      }}
                    />
                    {routeArray[currentIndex] && (
                      <Marker 
                        position={routeArray[currentIndex]}
                        icon={createCustomIcon()}
                      />
                    )}
                  </>
                )}
              </MapContainer>
            )}
          </div>

          {/* Map Overlay */}
          {!mapError && currentPoint?.address && (
            <div className="map-overlay">
              <div className="location-card-overlay">
                <div className="location-header">
                  <i className="bi bi-geo-alt-fill"></i>
                  <span>Current Location</span>
                </div>
                <div className="location-address">
                  {currentPoint.address}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DControlHistory;